//@ts-ignore
import { Product } from '../common/types/types'

interface ProductDescriptionProps {
   product_list: { [key: string]: Product }
   productCode: string
   useAltDescription: boolean
}

function ProductDescription({
   product_list,
   productCode,
   useAltDescription,
}: ProductDescriptionProps) {
   if (
      product_list &&
      productCode &&
      product_list.hasOwnProperty(productCode) &&
      (product_list[productCode].hasOwnProperty('ProductQuoteDesc') ||
         product_list[productCode].hasOwnProperty('AltProductQuoteDesc')) &&
      (product_list[productCode]['ProductQuoteDesc'] ||
         product_list[productCode]['AltProductQuoteDesc'])
   ) {
      const description = useAltDescription
         ? product_list[productCode]['AltProductQuoteDesc']
         : product_list[productCode]['ProductQuoteDesc']
      if (description !== null) {
         return (
            <div className='row'>
               <div dangerouslySetInnerHTML={{ __html: description }} />
            </div>
         )
      } else {
         return <></>
      }
   } else {
      if (!product_list) console.error('ERROR: product_list is null')
      else if (!productCode) console.error('ERROR: productCode is null')
      else if (!product_list.hasOwnProperty(productCode))
         console.error('ERROR: Product not found in product_list')
      else if (!product_list[productCode].hasOwnProperty('ProductQuoteDesc'))
         console.error(
            'ERROR: ProductQuoteDesc not found in product_list[productCode]',
         )
      else if (!product_list[productCode]['ProductQuoteDesc'])
         console.error(
            'ERROR: ProductQuoteDesc is null in product_list[productCode]',
         )
      else if (!product_list[productCode]['AltProductQuoteDesc'])
         console.error(
            'ERROR: AltProductQuoteDesc is null in product_list[productCode]',
         )
      else console.error('ERROR: ProductDescription Unknown error')
      return <></>
   }
}

interface ProductDescriptionsProps {
   product_list: { [key: string]: Product }
   coreProductSelected: string
   consultingServices: { serviceCode: string; count: number }[]
   hasCore: boolean
   hasPayroll: boolean
   hasBenAdmin: boolean
   hasTimeTracking: boolean
   hasTimeAttendance: boolean
   hasTimeClocks: boolean
   quoteType: string
   useAltDescription: boolean
   dynamicProducts: Map<string, number>
}

/**
 * A function that returns a React element that displays quote descriptions for a list of products.
 * The description is taken from the product_list object and is selected based on the productCode and
 * useAltDescription parameters.
 *
 * @param {Object} product_list - The product list object
 * @param {string} productCode - The code of the product to display the description for
 * @param {boolean} useAltDescription - Whether to use the AltProductQuoteDesc or ProductQuoteDesc
 * @param {boolean} coreProductSelected - Whether the core product is selected
 * @param {boolean} consultingServices - Whether consulting services are included
 * @param {boolean} hasCore - Whether the core product is included
 * @param {boolean} hasPayroll - Whether the payroll product is included
 * @param {boolean} hasBenAdmin - Whether the Benefits Administration product is included
 * @param {boolean} hasTimeTracking - Whether the Time Tracking product is included
 * @param {boolean} hasTimeClocks - Whether the Time Tracking with Time Clocks product is included
 * @param {string} quoteType - The type of quote
 *
 * @returns {ReactElement} A React element with the product description
 */
export default function ProductDescriptions({
   product_list,
   coreProductSelected,
   consultingServices,
   hasCore,
   hasPayroll,
   hasBenAdmin,
   hasTimeTracking,
   hasTimeAttendance,
   hasTimeClocks,
   quoteType,
   useAltDescription,
   dynamicProducts,
}: ProductDescriptionsProps) {
   let products = []
   if (hasCore && quoteType !== 'onetime')
      //Only add core desc if not core0 and not onetime quote
      products.push(coreProductSelected)
   if (hasPayroll && quoteType !== 'onetime') products.push('payroll')
   if (hasBenAdmin && quoteType !== 'onetime') products.push('benAdmin')
   if (hasTimeAttendance && quoteType !== 'onetime')
      products.push('timeAttendance')
   else if (hasTimeTracking && quoteType !== 'onetime')
      products.push('timeTracking')
   if (hasTimeClocks && quoteType !== 'onetime')
      products.push('clockMonthlySupport')
   if (consultingServices.length > 0 && quoteType !== 'onetime')
      consultingServices.forEach(
         (service) => service.count > 0 && products.push(service.serviceCode),
      )
   for (const [productCode, count] of dynamicProducts) {
      if (
         count > 0 &&
         quoteType !== 'onetime' &&
         product_list?.hasOwnProperty(productCode) &&
         product_list[productCode].hasOwnProperty('ProductQuoteDesc') &&
         product_list[productCode]['ProductQuoteDesc'] &&
         product_list[productCode]['ProductQuoteDesc'] !== '' &&
         product_list[productCode]['BillingFreq'] === 'monthly' &&
         !products.some(
            (p) =>
               product_list[productCode].ProductFamily &&
               product_list[p].ProductFamily ===
                  product_list[productCode].ProductFamily,
         )
      )
         //Filter so that if products have the same family, only one description will show
         products.push(productCode)
   }

   const productDescItems = products.map((product) => (
      <ProductDescription
         product_list={product_list}
         productCode={product}
         key={product}
         useAltDescription={useAltDescription}
      />
   ))

   return (
      <div className='container'>
         <div className='row'>
            <div className='col-6'>
               <div className='container'>
                  {productDescItems.filter((i, idx) => !(idx % 2))}
               </div>
            </div>
            <div className='col-6'>
               <div className='container'>
                  {productDescItems.filter((i, idx) => idx % 2)}
               </div>
            </div>
         </div>
      </div>
   )
}
